import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { Tag } from '@/components/ui/tag';
import { Copy, Edit, Save, X, RotateCcw } from 'lucide-react';
import MealPlanGrid from './MealPlanGrid';
import NutrientTracker from './NutrientTracker';
import NutrientCharts from './NutrientCharts';
import CopyDayModal from './CopyDayModal';
import { DeleteMealSlotOptions, FoodItem, MealSlot, MealType, DailyNutrients } from '@/types/meal-plan';
import { Client } from '@/types/client';
import { ClientIntakeData } from '@/components/meal-planning/ClientIntakeForm';
import { getValidAllergies, getValidCuisineTypes, getValidPreferences } from '@/lib/clientPreferenceOptions';
import { DrvPrimaryType } from '@/lib/api/drv';
import { DayNameEditPopover } from '@/components/meal-planning/DayNameEditPopover';
import { trackMealPlanDayCopyClicked } from '@/lib/analytics/trackers/mealPlanActions';

interface MealPlanMainContentProps {
  days: number[];
  activeDay: number;
  onActiveDayChange: (day: number) => void;
  getMealSlots: (day: number) => MealSlot[];
  onAddFood: (food: FoodItem, day: number, mealType: MealType, stateKey?: string) => void;
  onRemoveFood: (foodIndex: number, day: number, mealType: MealType, stateKey?: string) => void;
  onCopyDays?: (fromDay: number, toDays: number[]) => Promise<any | void>;
  onAddDateForCopy?: (fromDay: number) => Promise<number | null>;
  onSwapFood?: (oldFood: FoodItem, newFood: FoodItem, day: number, mealType: MealType, stateKey?: string) => void;
  onUpdateMealInfo?: (day: number, mealType: MealType, updates: { name?: string; timeSlot?: string; targetKcal?: number }, stateKey?: string) => void;
  clientPreferences?: Partial<ClientIntakeData>;
  clientId?: string;
  cuisineTypes?: string[];
  dayWisePreferences: { [day: number]: { dietaryPreferences?: string[]; cuisineTypes?: string[]; useClientDefaults?: boolean } };
  onSetDayPreferences: (day: number, field: 'dietaryPreferences' | 'cuisineTypes', value: string[]) => void;
  onResetDayPreferences: (day: number) => void;
  getPreferencesForDay: (day: number) => { allergies: string[]; dietaryPreferences: string[]; cuisineTypes: string[] };
  hasCustomPreferences: (day: number) => boolean;
  editingDay: number | null;
  onEditDay: (day: number | null) => void;
  calculateDailyNutrients: (day: number) => DailyNutrients;
  dayWiseNutrition?: any; // Nutrition data from API response
  clientData?: Client | null; // Client data with nutritional targets
  draftId?: string;
  onAddMealType?: (day: number, mealName: string, mealTime: string, targetCalories?: number | null) => Promise<void>;
  onDeleteMealSlot?: (day: number, mealName: string, options?: DeleteMealSlotOptions) => Promise<void>;
  onDraftDataUpdate?: (draftData: any) => void;
  isDeletingAnyMealSlot?: boolean;
  drvPrimaryTypes?: DrvPrimaryType[];
  /** Day display names (e.g. "Detox Day"). Used with onDayNameUpdate for edit UI. */
  dayNames?: Record<number, string | null>;
  /** Update day name via API; called after successful save. */
  onDayNameUpdate?: (day: number, dayName: string | null) => Promise<void>;
}

const MealPlanMainContent: React.FC<MealPlanMainContentProps> = ({
  days,
  activeDay,
  onActiveDayChange,
  getMealSlots,
  onAddFood,
  onRemoveFood,
  onCopyDays,
  onAddDateForCopy,
  onSwapFood,
  onUpdateMealInfo,
  clientPreferences,
  clientId,
  cuisineTypes = [],
  dayWisePreferences,
  onSetDayPreferences,
  onResetDayPreferences,
  getPreferencesForDay,
  hasCustomPreferences,
  editingDay,
  onEditDay,
  calculateDailyNutrients,
  dayWiseNutrition,
  clientData,
  draftId,
  onAddMealType,
  onDeleteMealSlot,
  onDraftDataUpdate,
  isDeletingAnyMealSlot = false,
  drvPrimaryTypes = [],
  dayNames,
  onDayNameUpdate,
}) => {
  // State for copy day modal
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyFromDay, setCopyFromDay] = useState<number | null>(null);
  const [editingDayNameFor, setEditingDayNameFor] = useState<number | null>(null);
  const [savingDayName, setSavingDayName] = useState(false);
  const handleCopyButtonClick = (fromDay: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCopyFromDay(fromDay);
    setCopyModalOpen(true);
  };

  const handleCopyDaysClick = async (fromDay: number, toDays: number[]) => {
    if (!onCopyDays) return;
    trackMealPlanDayCopyClicked({
      product_area: 'meal_plans',
      meal_plan_surface: 'manual_draft',
    });
    try {
      const updatedDraft = await onCopyDays(fromDay, toDays);
      if (updatedDraft && onDraftDataUpdate) {
        onDraftDataUpdate(updatedDraft);
      }
    } catch (error) {
      console.error('Error copying day(s):', error);
      throw error; // Re-throw to let the modal handle the error
    }
  };

  // Centralized helper to calculate all client targets
  const clientTargets = React.useMemo(() => {
    if (!clientData) return undefined;

    // Helper to get nutrition requirement value (Micros)
    const getMicroTarget = (category: 'vitamins' | 'minerals', nutrient: string) => {
      // 1. Try finding in DRV Requirements (New Structure)
      if (clientData?.drvRequirements?.micronutrients) {
        const { vitaminsFatSoluble, vitaminsWaterSoluble, mineralsMajor, mineralsTrace } = clientData.drvRequirements.micronutrients;
        const allMicros = [
          ...(vitaminsFatSoluble || []),
          ...(vitaminsWaterSoluble || []),
          ...(mineralsMajor || []),
          ...(mineralsTrace || [])
        ];
        
        const normalizedTarget = nutrient.replace(/_/g, '').toLowerCase();
        
        // Find matching nutrient requirement
        const req = allMicros.find(r => 
          r.nutrientId.replace(/_/g, '').toLowerCase() === normalizedTarget
        );
        
        if (req) {
          // Priority: RDA -> AI -> UL
          return req.rda ?? req.ai ?? req.ul ?? null;
        }
      }

      if (nutrient === 'vitaminA' || nutrient === 'vitamin_a') {
         console.warn(`[getMicroTarget] No data found for ${nutrient} in DRV requirements`);
      }
      return null;
    };

    // Helper to get macro target (grams)
    const getMacroTarget = (type: 'protein' | 'carbohydrates' | 'total_fat' | 'totalFat') => {
      // 1. clientGoals (New API structure)
      if (clientData?.clientGoals) {
         const goals = clientData.clientGoals;
         if (type === 'protein') return goals.protein?.grams?.value ?? goals.protein?.value;
         if (type === 'carbohydrates') return goals.carbohydrates?.grams?.value;
         // Handle both total_fat and totalFat keys
         if (type === 'total_fat' || type === 'totalFat') return goals.totalFat?.grams?.value ?? goals.totalFat?.value;
      }
      
      // 2. DRV Requirements (Fallback for Macros)
      if (clientData?.drvRequirements?.macronutrients) {
         const { carbohydrates, proteins, lipids } = clientData.drvRequirements.macronutrients;
         let list: any[] = [];
         let idToCheck = '';
         
         if (type === 'carbohydrates') { list = carbohydrates || []; idToCheck = 'carbohydrate'; }
         if (type === 'protein') { list = proteins || []; idToCheck = 'protein'; }
         if (type === 'total_fat' || type === 'totalFat') { list = lipids || []; idToCheck = 'total_fat'; }
         
         const req = list.find(r => r.nutrientId === idToCheck);
         if (req) {
            // Priority: RDA -> AI -> EAR
            return req.rda ?? req.ai ?? req.ear ?? null;
         }
      }
      
      return null;
    };

    const getCaloriesTarget = () => {
       return clientData?.eerOverride 
           ?? clientData?.eer
           ?? 2000;
    };


    return {
      calories: getCaloriesTarget(),
      protein: getMacroTarget('protein') ?? 0,
      carbs: getMacroTarget('carbohydrates') ?? 0,
      fat: getMacroTarget('total_fat') ?? 0,
      vitaminA: getMicroTarget('vitamins', 'vitaminA') || 0,
      vitaminC: getMicroTarget('vitamins', 'vitaminC') || 0,
      calcium: getMicroTarget('minerals', 'calcium') || 0,
      iron: getMicroTarget('minerals', 'iron') || 0,
    };
  }, [clientData, drvPrimaryTypes]);

  const toFiniteNumber = (value: unknown): number | undefined => {
    const parsed = typeof value === 'string' ? Number(value) : value;
    return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : undefined;
  };

  const extractQuantity = (value: unknown): number | undefined => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }
    if (value && typeof value === 'object') {
      return toFiniteNumber((value as { quantity?: unknown }).quantity);
    }
    return undefined;
  };

  const pickNumber = (...values: unknown[]): number => {
    for (const value of values) {
      const quantity = extractQuantity(value);
      if (quantity !== undefined) return quantity;
      const numeric = toFiniteNumber(value);
      if (numeric !== undefined) return numeric;
    }
    return 0;
  };

  const hasMacroData = (nutrients: Pick<DailyNutrients, 'totalCalories' | 'totalProtein' | 'totalCarbs' | 'totalFat' | 'totalFiber'>): boolean => {
    return (
      nutrients.totalCalories > 0 ||
      nutrients.totalProtein > 0 ||
      nutrients.totalCarbs > 0 ||
      nutrients.totalFat > 0 ||
      nutrients.totalFiber > 0
    );
  };

  const getDisplayNutrients = (day: number, hasAnyRecipes: boolean): DailyNutrients => {
    const localNutrients = calculateDailyNutrients(day);

    const dayWiseEntry = Array.isArray(dayWiseNutrition)
      ? dayWiseNutrition.find((entry: { day?: unknown; dayNumber?: unknown } | undefined) =>
          Number(entry?.day ?? entry?.dayNumber) === day
        )
      : dayWiseNutrition?.[`day${day}`] ?? dayWiseNutrition?.[day];

    const apiNutritionRaw = dayWiseEntry?.nutrition ?? dayWiseEntry;
    const apiNutrition =
      apiNutritionRaw && typeof apiNutritionRaw === 'object'
        ? (apiNutritionRaw as Record<string, unknown>)
        : {};
    const apiMacrosRaw = apiNutrition.macros;
    const apiMacros =
      apiMacrosRaw && typeof apiMacrosRaw === 'object'
        ? (apiMacrosRaw as Record<string, unknown>)
        : {};
    const apiMicrosRaw = apiNutrition.micros;
    const apiMicros =
      apiMicrosRaw && typeof apiMicrosRaw === 'object'
        ? (apiMicrosRaw as Record<string, unknown>)
        : {};
    const apiMicrosVitaminsRaw = apiMicros.vitamins;
    const apiMicrosVitamins =
      apiMicrosVitaminsRaw && typeof apiMicrosVitaminsRaw === 'object'
        ? (apiMicrosVitaminsRaw as Record<string, unknown>)
        : {};
    const apiMicrosMineralsRaw = apiMicros.minerals;
    const apiMicrosMinerals =
      apiMicrosMineralsRaw && typeof apiMicrosMineralsRaw === 'object'
        ? (apiMicrosMineralsRaw as Record<string, unknown>)
        : {};

    const apiNutrients: DailyNutrients = {
      totalCalories: pickNumber(apiNutrition.totalCalories, apiNutrition.calories, apiMacros.calories),
      totalProtein: pickNumber(apiNutrition.totalProtein, apiNutrition.protein, apiMacros.protein),
      totalCarbs: pickNumber(
        apiNutrition.totalCarbs,
        apiNutrition.carbs,
        apiNutrition.carbohydrate,
        apiMacros.carbs,
        apiMacros.carbohydrate,
      ),
      totalFat: pickNumber(
        apiNutrition.totalFat,
        apiNutrition.fat,
        apiNutrition.total_fat,
        apiMacros.fat,
        apiMacros.total_fat,
      ),
      totalFiber: pickNumber(
        apiNutrition.totalFiber,
        apiNutrition.fiber,
        apiNutrition.dietary_fibre,
        apiMacros.fiber,
        apiMacros.dietary_fibre,
      ),
      totalSodium: pickNumber(apiNutrition.totalSodium, apiNutrition.sodium, apiMacros.sodium),
      totalSugar: pickNumber(apiNutrition.totalSugar, apiNutrition.sugar, apiMacros.sugar),
      totalCholesterol: pickNumber(apiNutrition.totalCholesterol, apiNutrition.cholesterol, apiMacros.cholesterol),
      totalCalcium: pickNumber(apiNutrition.totalCalcium, apiNutrition.calcium, apiMicrosMinerals.calcium, apiMicros.calcium),
      totalIron: pickNumber(apiNutrition.totalIron, apiNutrition.iron, apiMicrosMinerals.iron, apiMicros.iron),
      micronutrients: {
        vitaminA: pickNumber(
          apiMicrosVitamins.vitaminA,
          apiMicrosVitamins.vitamin_a,
          apiMicros.vitaminA,
          apiMicros.vitamin_a,
          apiNutrition.vitaminA,
          apiNutrition.vitamin_a,
        ),
        vitaminC: pickNumber(
          apiMicrosVitamins.vitaminC,
          apiMicrosVitamins.vitamin_c,
          apiMicros.vitaminC,
          apiMicros.vitamin_c,
          apiNutrition.vitaminC,
          apiNutrition.vitamin_c,
        ),
        vitaminD: pickNumber(
          apiMicrosVitamins.vitaminD,
          apiMicrosVitamins.vitamin_d,
          apiMicros.vitaminD,
          apiMicros.vitamin_d,
          apiNutrition.vitaminD,
          apiNutrition.vitamin_d,
        ),
        calcium: pickNumber(apiMicrosMinerals.calcium, apiMicros.calcium, apiNutrition.calcium),
        iron: pickNumber(apiMicrosMinerals.iron, apiMicros.iron, apiNutrition.iron),
        sodium: pickNumber(apiNutrition.totalSodium, apiNutrition.sodium, apiMacros.sodium),
      },
    };

    const useLocalMacros =
      hasAnyRecipes || hasMacroData(localNutrients) || !hasMacroData(apiNutrients);
    const macroSource = useLocalMacros ? localNutrients : apiNutrients;
    const localMicronutrients = localNutrients.micronutrients || {};
    const apiMicronutrients = apiNutrients.micronutrients || {};

    return {
      totalCalories: macroSource.totalCalories || 0,
      totalProtein: macroSource.totalProtein || 0,
      totalCarbs: macroSource.totalCarbs || 0,
      totalFat: macroSource.totalFat || 0,
      totalFiber: macroSource.totalFiber || apiNutrients.totalFiber || 0,
      totalSodium: apiNutrients.totalSodium || localNutrients.totalSodium || 0,
      totalSugar: apiNutrients.totalSugar || localNutrients.totalSugar || 0,
      totalCholesterol: apiNutrients.totalCholesterol || localNutrients.totalCholesterol || 0,
      totalCalcium: apiNutrients.totalCalcium || localNutrients.totalCalcium || 0,
      totalIron: apiNutrients.totalIron || localNutrients.totalIron || 0,
      calories: macroSource.totalCalories || 0,
      protein: macroSource.totalProtein || 0,
      carbs: macroSource.totalCarbs || 0,
      fat: macroSource.totalFat || 0,
      micronutrients: {
        vitaminA: apiMicronutrients.vitaminA || localMicronutrients.vitaminA || 0,
        vitaminC: apiMicronutrients.vitaminC || localMicronutrients.vitaminC || 0,
        vitaminD: apiMicronutrients.vitaminD || localMicronutrients.vitaminD || 0,
        calcium: apiMicronutrients.calcium || localMicronutrients.calcium || 0,
        iron: apiMicronutrients.iron || localMicronutrients.iron || 0,
        sodium: apiMicronutrients.sodium || localMicronutrients.sodium || 0,
      },
    };
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeDay.toString()} onValueChange={(value) => onActiveDayChange(parseInt(value))}>
            <TabsList className="w-full inline-flex h-11 items-center justify-start rounded-md bg-muted p-1 text-muted-foreground">
              {days.map(day => {
                const displayName = (dayNames && dayNames[day]?.trim()) || `Day ${day}`;
                const canEditDayName = draftId && onDayNameUpdate;
                return (
                <div key={day} className="flex-1 relative group flex items-center justify-center gap-1 min-w-0">
                  <TabsTrigger value={day.toString()} className="w-full whitespace-nowrap pr-10 flex items-center gap-1">
                    <span className="truncate">{displayName}</span>
                  </TabsTrigger>
                  {canEditDayName && (
                    <DayNameEditPopover
                      open={editingDayNameFor === day}
                      onOpenChange={(open) => {
                        if (!open) setEditingDayNameFor(null);
                        else setEditingDayNameFor(day);
                      }}
                      dayNumber={day}
                      value={dayNames?.[day] ?? ''}
                      saving={savingDayName}
                      hasExistingName={!!(dayNames?.[day]?.trim())}
                      triggerClassName="absolute right-8 top-1/2 z-10 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-accent-foreground group-hover:inline-flex group-focus-within:inline-flex"
                      onSave={async (valueToSave) => {
                        setSavingDayName(true);
                        try {
                          await onDayNameUpdate!(day, valueToSave || null);
                          setEditingDayNameFor(null);
                        } finally {
                          setSavingDayName(false);
                        }
                      }}
                      onClear={async () => {
                        setSavingDayName(true);
                        try {
                          await onDayNameUpdate!(day, null);
                          setEditingDayNameFor(null);
                        } finally {
                          setSavingDayName(false);
                        }
                      }}
                    />
                  )}
                  {onCopyDays && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 z-10 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-accent-foreground group-hover:inline-flex group-focus-within:inline-flex"
                      title={days.length > 1 ? "Copy this day to next day" : "Add another day with this day's meals"}
                      aria-label={days.length > 1 ? "Copy this day to next day" : "Add another day with this day's meals"}
                      onClick={(e) => handleCopyButtonClick(day, e)}
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
              })}
            </TabsList>
            
            {days.map(day => {
              const dayPrefs = getPreferencesForDay(day);
              const isEditingThisDay = editingDay === day;
              const hasCustom = hasCustomPreferences(day);
              const dayMealSlots = getMealSlots(day);
              const hasAnyRecipes = dayMealSlots.some(slot => slot.items.length > 0);
              const dayNutrients = getDisplayNutrients(day, hasAnyRecipes);

              return (
              <TabsContent key={day} value={day.toString()}>
                  <div className="grid grid-cols-1 lg:grid-cols-[350px_1fr] gap-4">
                    {/* Left Side - Nutrition */}
                    <div className="space-y-4">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base font-semibold">Nutrition</CardTitle>
                          <p className="text-xs text-gray-500 mt-1">Day {day} Daily Totals</p>
                        </CardHeader>
                        <CardContent>
                          <NutrientTracker 
                            nutrients={dayNutrients}
                            targets={clientTargets}

                          />
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent>
                          <NutrientCharts 
                            nutrients={dayNutrients}
                            targets={clientTargets}
                          />
                        </CardContent>
                      </Card>
                    </div>

                    {/* Right Side - Preferences and Meals */}
                    <div className="space-y-4">
                      {/* Day-wise Preferences Section */}
                      <Card className={hasCustom ? "border-2 border-purple-300 bg-purple-50/30" : "border border-gray-200"}>
                    <CardContent className="p-4">
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-gray-900">
                            Day {day} Preferences
                          </h4>
                          {hasCustom && (
                            <Tag variant="outlined" size="sm" className="bg-purple-100 text-purple-700">
                              Custom
                            </Tag>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {!hasAnyRecipes && (
                            isEditingThisDay ? (
                              <>
                                <Button size="sm" variant="outline" onClick={() => onEditDay(null)}>
                                  <Save className="h-3 w-3 mr-1" />
                                  Done
                                </Button>
                                {hasCustom && (
                                  <Button size="sm" variant="ghost" onClick={() => onResetDayPreferences(day)}>
                                    <RotateCcw className="h-3 w-3 mr-1" />
                                    Reset
                                  </Button>
                                )}
                              </>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => onEditDay(day)}>
                                <Edit className="h-3 w-3 mr-1" />
                                {hasCustom ? 'Edit' : 'Customize'}
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                        {isEditingThisDay && (
                          <p className="text-xs text-purple-600 italic">
                            💡 You can customize dietary preferences and cuisine types for this day. Allergies are always from client defaults.
                          </p>
                          )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {/* Allergies - Always Read Only */}
                        <div className="space-y-2">
                          <Label className="text-xs font-medium text-gray-600">
                            Allergies 
                            <span className="text-xs text-gray-500 ml-1">(From Client)</span>
                          </Label>
                          {dayPrefs.allergies.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {dayPrefs.allergies.map((allergy, idx) => {
                                const allergyOption = getValidAllergies().find(a => a.value === allergy);
                                const displayName = allergyOption ? allergyOption.name : allergy;
                                return (
                                  <Tag key={`allergy-${idx}`} variant="danger" size="sm">
                                    {displayName}
                                  </Tag>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 italic">None</p>
                          )}
                        </div>

                        {/* Dietary Preferences - Editable */}
                        <div className="space-y-2">
                          <Label className="text-xs font-medium text-gray-600">Dietary Preferences</Label>
                          {isEditingThisDay ? (
                            <MultiSelect
                              options={getValidPreferences().map(p => ({ id: p.value, name: p.name }))}
                              selected={dayPrefs.dietaryPreferences}
                              onChange={(value) => onSetDayPreferences(day, 'dietaryPreferences', value)}
                              placeholder="Add dietary preferences..."
                              className="text-xs"
                            />
                          ) : dayPrefs.dietaryPreferences.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {dayPrefs.dietaryPreferences.map((pref, idx) => {
                                const prefOption = getValidPreferences().find(p => p.value === pref);
                                const displayName = prefOption ? prefOption.name : pref;
                                return (
                                  <Tag key={`pref-${idx}`} variant="success" size="sm">
                                    {displayName}
                                  </Tag>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 italic">None</p>
                          )}
                        </div>

                        {/* Cuisine Types - Editable */}
                        <div className="space-y-2">
                          <Label className="text-xs font-medium text-gray-600">Cuisine Types</Label>
                          {isEditingThisDay ? (
                            <MultiSelect
                              options={getValidCuisineTypes().map(c => ({ id: c.value, name: c.name }))}
                              selected={dayPrefs.cuisineTypes}
                              onChange={(value) => onSetDayPreferences(day, 'cuisineTypes', value)}
                              placeholder="Add cuisines..."
                              className="text-xs"
                            />
                          ) : dayPrefs.cuisineTypes.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {dayPrefs.cuisineTypes.map((cuisine, idx) => {
                                const cuisineOption = getValidCuisineTypes().find(c => c.value === cuisine);
                                const displayName = cuisineOption ? cuisineOption.name : cuisine;
                                return (
                                <Tag key={idx} variant="info" size="sm">
                                    {displayName}
                                </Tag>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 italic">None</p>
                          )}
                        </div>
                      </div>
                      </CardContent>
                    </Card>
                  
                  <MealPlanGrid 
                        mealSlots={dayMealSlots}
                    onAddFood={onAddFood}
                    onRemoveFood={onRemoveFood}
                    selectedDay={day}
                    clientPreferences={clientPreferences}
                    onSwapFood={onSwapFood}
                    onUpdateMealInfo={onUpdateMealInfo}
                    onDeleteMealSlot={onDeleteMealSlot}
                    onDraftDataUpdate={onDraftDataUpdate}
                    clientId={clientId}
                        cuisineTypes={dayPrefs.cuisineTypes}
                        draftId={draftId}
                        onAddMealType={onAddMealType}
                        allergies={dayPrefs.allergies}
                        dietaryPreferences={dayPrefs.dietaryPreferences}
                        isDeletingAnyMealSlot={isDeletingAnyMealSlot}
                  />
                    </div>
                </div>
              </TabsContent>
              );
            })}
          </Tabs>
        </CardContent>
      </Card>

      {/* Copy Day Modal */}
      {copyFromDay && (
        <CopyDayModal
          key={copyFromDay}
          open={copyModalOpen}
          onOpenChange={setCopyModalOpen}
          fromDay={copyFromDay}
          availableDays={days}
          onCopyDays={(from, to) => handleCopyDaysClick(from, to)}
          onAddDateForCopy={onAddDateForCopy}
        />
      )}
    </div>
  );
};

export default MealPlanMainContent;
